/**
 * useDocumentDialog Hook
 * Main orchestration hook for DocumentDialog
 * Combines file upload, form state, and submission logic
 *
 * The file tab runs a queue: files are uploaded one after another
 * (UPLOAD_CONCURRENCY) through the XHR helper, each row shows its own status
 * and byte progress, a 409 duplicate pauses the queue until the user picks a
 * resolution, and failed rows can be retried without re-selecting files.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { DocumentCreate } from '../../api/ApiClient';
import { useDocument, useUploadDocument, useUpdateDocument, useUpdateDocumentFile } from '../api/useDocuments';
import { log } from '../../utils/errors/logger';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useFileUpload } from './useFileUpload';
import { useDocumentForm } from './useDocumentForm';
import { useUploadCapabilities } from './useUploadCapabilities';
import {
  MAX_TITLE_LENGTH,
  UploadItem,
  allSucceeded,
  mapUploadError,
  nextPending,
  prepareRetry,
  stripExtension,
  summarize,
} from './uploadQueue';
import { CreateDocTabValue, UseDocumentDialogOptions, UseDocumentDialogReturn } from '../../components/documents/DocumentDialog/types';
import { DuplicateInfo } from '../../components/documents/DuplicateUploadDialog';
import { signedInSourceOf } from '../../utils/documents/signedInSource';

/**
 * Get source type based on tab value
 */
const getSourceTypeForTab = (tabValue: number): string => {
  switch (tabValue) {
    case 0: return 'manual'; // Text content
    case 1: return 'file';   // File upload
    case 2: return 'url';    // URL import
    default: return 'manual';
  }
};

const parseCsvIds = (value: string): string[] =>
  value.split(',').map(item => item.trim()).filter(Boolean);

interface DuplicateSignal {
  code?: string;
  duplicate_kind?: string;
  existing_document_id?: string | null;
  existing_title?: string | null;
  owned_by_other?: boolean;
}

/** Read the structured 409 body the upload helper and http-client both reject with. */
function readDuplicateSignal(error: unknown): DuplicateSignal | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const err = error as { status?: number; error?: { detail?: DuplicateSignal }; detail?: DuplicateSignal };
  const detail = err.error?.detail ?? err.detail;
  if (err.status === 409 && detail?.code === 'duplicate_document') {
    return detail;
  }
  return null;
}

/**
 * Main hook for DocumentDialog logic
 */
export const useDocumentDialog = ({
  open,
  editingDoc,
  defaultProvider,
  onClose,
  onSubmit
}: UseDocumentDialogOptions): UseDocumentDialogReturn => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess, showWarning } = useSnackbar();
  const { capabilities } = useUploadCapabilities();

  // TanStack Query mutations
  const uploadDocumentMutation = useUploadDocument();
  const updateDocumentMutation = useUpdateDocument();
  const updateDocumentFileMutation = useUpdateDocumentFile();

  // Tab state
  const [activeTab, setActiveTab] = useState<CreateDocTabValue>(0);
  // The title is kept per source tab: a title filled from a file name stays
  // on the file tab, a title typed for a URL stays on the URL tab.
  const titleByTabRef = useRef<Partial<Record<CreateDocTabValue, string>>>({});

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Duplicate-upload prompt: set from a 409 response so the dialog can ask the
  // user how to resolve (update existing / upload copy / cancel). The queue
  // stays paused on that row until the user answers.
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateInfo | null>(null);
  const duplicateItemIdRef = useRef<string | null>(null);
  // In-flight XHR of the current row; aborted when the dialog is cancelled.
  const abortRef = useRef<AbortController | null>(null);
  const stopRef = useRef(false);

  const isEditMode = Boolean(editingDoc);

  // Use custom hooks
  const fileUpload = useFileUpload({
    multiple: !isEditMode,
    maxFileSizeMB: capabilities.max_file_size_mb,
    allowedExtensions: capabilities.allowed_extensions,
  });
  const { data: fullDocument, isLoading: isLoadingDocument } = useDocument(
    editingDoc?.document_id || (editingDoc as any)?.id || ''
  );

  const uploadSummary = useMemo(() => summarize(fileUpload.items), [fileUpload.items]);
  const updateUploadItem = fileUpload.updateItem;

  // The title auto-fills from the file name only while exactly one file is
  // queued; with several files each one is titled by its own name.
  const titleSourceFile = useMemo(() => {
    if (isEditMode) {
      return fileUpload.selectedFile;
    }
    return fileUpload.items.length === 1 ? fileUpload.items[0].file : null;
  }, [fileUpload.items, fileUpload.selectedFile, isEditMode]);

  const documentForm = useDocumentForm({
    editingDoc,
    defaultProvider,
    fullDocument,
    selectedFile: titleSourceFile
  });

  const showTitleField = !(activeTab === 1 && !isEditMode && fileUpload.items.length > 1);

  /**
   * Initialize/reset state when dialog opens/closes
   */
  useEffect(() => {
    if (open) {
      // Reset submission state
      setSubmitError(null);
      setIsSubmitting(false);

      // Set tab based on document source type (edit mode)
      if (editingDoc) {
        log.debug('DocumentDialog: setting tab for editing document', {
          title: editingDoc.title,
          source_type: editingDoc.source_type,
          file_type: editingDoc.file_type
        });
        if (editingDoc.source_type === 'file') {
          setActiveTab(1); // File tab
        } else if (editingDoc.source_type === 'url') {
          setActiveTab(2); // URL tab
        } else {
          // Text tab for 'manual', 'custom', or any other type
          setActiveTab(0);
        }
      } else {
        setActiveTab(0); // Default to text tab for new documents
        fileUpload.resetFile();
      }
    }
    // fileUpload is intentionally excluded to prevent effect from re-running on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingDoc]);

  /**
   * Reset form state when dialog opens/closes
   * This mimics the FAQ dialog pattern that works correctly
   */
  useEffect(() => {
    if (open) {
      // When opening, ensure form is reset with current data
      documentForm.resetForm();
      stopRef.current = false;
    } else {
      // When closing, explicitly clean up all state
      documentForm.resetForm();
      fileUpload.resetFile();
      titleByTabRef.current = {};
      setActiveTab(0);
      setSubmitError(null);
      setIsSubmitting(false);
      setDuplicateInfo(null);
      duplicateItemIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingDoc]);

  /**
   * Edit the title of a queued upload row (multi-file batch).
   */
  const handleUploadItemTitleChange = useCallback((id: string, title: string) => {
    updateUploadItem(id, { title: title.slice(0, MAX_TITLE_LENGTH) });
  }, [updateUploadItem]);

  /**
   * Handle tab change
   */
  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    const nextTab = newValue as CreateDocTabValue;
    setActiveTab((currentTab) => {
      if (currentTab === nextTab) {
        return currentTab;
      }
      titleByTabRef.current[currentTab] = documentForm.formState.title;
      const restored = titleByTabRef.current[nextTab] ?? '';
      documentForm.setFormState((prev) => ({ ...prev, title: restored }));
      // An empty title on the target tab may be filled from a file again.
      documentForm.setTitleManuallyEdited(restored !== '');
      return nextTab;
    });
  }, [documentForm.formState.title, documentForm.setFormState, documentForm.setTitleManuallyEdited]);

  /** What the primary button does on the file tab. */
  const fileAction: 'upload' | 'retry' =
    uploadSummary.pending === 0 && uploadSummary.failed > 0 ? 'retry' : 'upload';

  /**
   * Validate form - memoized to reactively update when dependencies change
   */
  const isValid = useMemo((): boolean => {
    if (!documentForm.formState.visibility) {
      return false;
    }
    // Title is required except for a multi-file batch (each file gets its own).
    if (showTitleField && !documentForm.formState.title) {
      return false;
    }

    if (editingDoc) {
      // **EDIT MODE** - Allow metadata-only edits for ALL document types
      // Users can update title, visibility, sharing without changing content
      // No additional validation required beyond title and visibility

    } else {
      // **CREATE MODE** - Strict validation required
      if (activeTab === 0 && !documentForm.formState.content) {
        return false;
      }
      if (activeTab === 1) {
        const retryable = fileUpload.items.some(
          (item) => item.status === 'error' && item.retryable !== false
        );
        if (uploadSummary.pending === 0 && !retryable) {
          return false;
        }
      }
      if (activeTab === 2 && !documentForm.formState.url) {
        return false;
      }
      // A signed-in source cannot be fetched from here; the tab explains why.
      if (activeTab === 2 && signedInSourceOf(documentForm.formState.url)) {
        return false;
      }
    }

    // Prevent submission during upload or double-submit
    return !fileUpload.uploadingFile && !isSubmitting;
  }, [
    documentForm.formState,
    editingDoc,
    activeTab,
    showTitleField,
    fileUpload.items,
    fileUpload.uploadingFile,
    uploadSummary.pending,
    isSubmitting,
  ]);

  const aclSettings = useMemo(() => ({
    visibility: documentForm.formState.visibility,
    shared_with_users: parseCsvIds(documentForm.formState.shared_with_users),
    shared_with_groups: parseCsvIds(documentForm.formState.shared_with_groups)
  }), [
    documentForm.formState.visibility,
    documentForm.formState.shared_with_users,
    documentForm.formState.shared_with_groups
  ]);

  /**
   * Upload a single queue row. Returns the duplicate signal when the backend
   * answered 409 (the queue then pauses), otherwise null. Errors are written
   * into the row instead of thrown so one bad file never stops the batch.
   */
  const uploadOne = useCallback(
    async (item: UploadItem, onDuplicate: 'prompt' | 'update' | 'copy'): Promise<DuplicateInfo | null> => {
      const controller = new AbortController();
      abortRef.current = controller;
      fileUpload.updateItem(item.id, { status: 'uploading', progress: 0, error: undefined });

      // One queued file: the shared title field applies. Several files: each
      // row carries its own editable title, falling back to the file name.
      const useFormTitle = fileUpload.itemsRef.current.length === 1 && documentForm.formState.title;
      const title = useFormTitle
        ? documentForm.formState.title
        : (item.title?.trim() || stripExtension(item.file.name));

      try {
        const doc = await uploadDocumentMutation.mutateAsync({
          file: item.file,
          title,
          aclSettings,
          textExtractionProvider: documentForm.formState.text_extraction_provider,
          onDuplicate,
          onProgress: (percent) => fileUpload.updateItem(item.id, { progress: percent }),
          signal: controller.signal,
        });
        fileUpload.updateItem(item.id, {
          status: 'done',
          progress: 100,
          documentId: doc.document_id ?? undefined,
        });
        return null;
      } catch (error: unknown) {
        const duplicate = readDuplicateSignal(error);
        if (duplicate) {
          fileUpload.updateItem(item.id, { status: 'duplicate', progress: 0 });
          duplicateItemIdRef.current = item.id;
          return {
            duplicateKind: duplicate.duplicate_kind ?? 'content',
            existingDocumentId: duplicate.existing_document_id,
            existingTitle: duplicate.existing_title,
            ownedByOther: Boolean(duplicate.owned_by_other),
          };
        }
        if (controller.signal.aborted) {
          fileUpload.updateItem(item.id, { status: 'skipped', progress: 0 });
          return null;
        }
        log.error('Upload failed for queued file', {
          name: item.file.name,
          status: (error as { status?: number })?.status,
        });
        fileUpload.updateItem(item.id, {
          status: 'error',
          progress: 0,
          error: mapUploadError(error, (key, options) => t(key, options as Record<string, unknown>) as string),
        });
        return null;
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    },
    [aclSettings, documentForm.formState, fileUpload, t, uploadDocumentMutation]
  );

  /**
   * Called once the queue has no pending rows left: refresh the list a single
   * time, then either close (everything succeeded) or keep the dialog open so
   * the failed rows stay visible and can be retried.
   */
  const finishQueue = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['documents'] });
    const items = fileUpload.itemsRef.current;
    const summary = summarize(items);
    if (allSucceeded(items)) {
      if (summary.done > 0) {
        showSuccess(t('documents.upload.summary.allDone', { count: summary.done }));
      }
      onClose();
      return;
    }
    if (summary.done > 0 || summary.failed > 0) {
      showWarning(t('documents.upload.summary.partial', { done: summary.done, failed: summary.failed }));
    }
  }, [fileUpload.itemsRef, onClose, queryClient, showSuccess, showWarning, t]);

  /**
   * Drain the queue sequentially. Reads the synchronous itemsRef on every
   * iteration so rows added, removed or resolved while awaiting are seen.
   */
  const runQueue = useCallback(async () => {
    fileUpload.setUploadingFile(true);
    try {
      let item = nextPending(fileUpload.itemsRef.current);
      while (item && !stopRef.current) {
        const duplicate = await uploadOne(item, 'prompt');
        if (duplicate) {
          setDuplicateInfo(duplicate);
          return; // paused; resolveDuplicate / cancelDuplicate resume the loop
        }
        item = nextPending(fileUpload.itemsRef.current);
      }
      if (!stopRef.current) {
        await finishQueue();
      }
    } finally {
      fileUpload.setUploadingFile(false);
    }
  }, [fileUpload, finishQueue, uploadOne]);

  /**
   * Handle document submission
   */
  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (editingDoc) {
        // **EDIT MODE**
        if (editingDoc.source_type === 'file' && fileUpload.selectedFile) {
          // File replacement - new file selected
          fileUpload.setUploadingFile(true);

          // Simulate upload progress
          const progressInterval = setInterval(() => {
            fileUpload.setUploadProgress(prev => {
              const newProgress = prev + Math.random() * 15;
              return newProgress >= 90 ? 90 : newProgress;
            });
          }, 300);

          try {
            await updateDocumentFileMutation.mutateAsync({
              documentId: editingDoc.document_id || (editingDoc as any).id,
              file: fileUpload.selectedFile,
              title: documentForm.formState.title,
              aclSettings,
              textExtractionProvider: documentForm.formState.text_extraction_provider
            });
            clearInterval(progressInterval);
            fileUpload.setUploadProgress(100);
            setTimeout(() => fileUpload.setUploadingFile(false), 500);
          } catch (error) {
            clearInterval(progressInterval);
            fileUpload.setUploadingFile(false);
            throw error;
          }
        } else {
          // Update metadata only (no new file OR text/url documents)
          const updateData: any = {
            title: documentForm.formState.title,
            visibility: documentForm.formState.visibility,
            source_type: editingDoc.source_type, // Preserve existing source_type
            file_type: editingDoc.file_type, // Preserve existing file_type
            shared_with_users: aclSettings.shared_with_users,
            shared_with_groups: aclSettings.shared_with_groups
          };

          // Add content for text documents (manual or custom)
          const isTextDocument = editingDoc.source_type === 'manual' || editingDoc.source_type === 'custom';
          if (isTextDocument) {
            updateData.content = documentForm.formState.content;
          }

          // Add URL for URL documents
          if (editingDoc.source_type === 'url') {
            updateData.url = documentForm.formState.url;
          }

          await updateDocumentMutation.mutateAsync({
            documentId: editingDoc.document_id!,
            data: updateData
          });
        }
        onClose();
        return;
      }

      // **CREATE MODE**
      if (activeTab === 0) {
        // Text content
        if (!documentForm.formState.content) {
          throw new Error(t('documents.notification.contentRequired'));
        }
        const docData: DocumentCreate = {
          title: documentForm.formState.title,
          content: documentForm.formState.content,
          visibility: documentForm.formState.visibility as any,
          source_type: getSourceTypeForTab(0) as any,
          url: '',
          shared_with_users: aclSettings.shared_with_users,
          shared_with_groups: aclSettings.shared_with_groups
        };
        await onSubmit(docData, false);
        onClose();
      } else if (activeTab === 1) {
        // File upload queue. A retry re-queues the failed rows first.
        if (fileAction === 'retry') {
          fileUpload.replaceItems(prepareRetry);
        }
        if (!nextPending(fileUpload.itemsRef.current)) {
          throw new Error(t('documents.notification.fileRequired'));
        }
        stopRef.current = false;
        await runQueue();
        // runQueue closes the dialog itself when every row succeeded.
      } else if (activeTab === 2) {
        // URL content
        if (!documentForm.formState.url) {
          throw new Error(t('documents.notification.urlRequired'));
        }
        const docData: DocumentCreate = {
          title: documentForm.formState.title,
          url: documentForm.formState.url,
          visibility: documentForm.formState.visibility as any,
          source_type: getSourceTypeForTab(2) as any,
          content: '',
          shared_with_users: aclSettings.shared_with_users,
          shared_with_groups: aclSettings.shared_with_groups
        };
        await onSubmit(docData, false);
        onClose();
      }
    } catch (error: any) {
      log.error('Error submitting document', { error: error?.message });
      // Client-side validation throws carry an already-localized message (the
      // *Required keys), so surface those verbatim. Backend/network errors carry
      // an English HTTP detail, which must not be shown raw — fall back to the
      // localized saveFailed message for those.
      const localizedRequired = [
        t('documents.notification.contentRequired'),
        t('documents.notification.fileRequired'),
        t('documents.notification.urlRequired'),
      ];
      const rawMessage = typeof error?.message === 'string' ? error.message : '';
      setSubmitError(
        localizedRequired.includes(rawMessage)
          ? rawMessage
          : t('documents.notification.saveFailed', 'Failed to save document')
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    editingDoc,
    activeTab,
    documentForm.formState,
    fileUpload,
    fileAction,
    updateDocumentMutation,
    updateDocumentFileMutation,
    aclSettings,
    onSubmit,
    onClose,
    runQueue,
    t
  ]);

  /**
   * Resolve a duplicate prompt for the paused row: re-run that upload as
   * "update" (re-use existing) or "copy" (force a distinct document), then
   * continue with the rest of the queue.
   */
  const resolveDuplicate = useCallback(
    async (resolution: 'update' | 'copy') => {
      const itemId = duplicateItemIdRef.current;
      setDuplicateInfo(null);
      duplicateItemIdRef.current = null;
      const item = fileUpload.itemsRef.current.find((row) => row.id === itemId);
      if (!item) {
        return;
      }
      fileUpload.setUploadingFile(true);
      try {
        const again = await uploadOne(item, resolution);
        if (again) {
          // The backend answered 409 a second time; leave the row as duplicate.
          fileUpload.updateItem(item.id, { status: 'error', error: t('documents.notification.saveFailed') });
        }
      } finally {
        fileUpload.setUploadingFile(false);
      }
      await runQueue();
    },
    [fileUpload, runQueue, t, uploadOne]
  );

  /** Skip the duplicate row and continue with the remaining files. */
  const cancelDuplicate = useCallback(() => {
    const itemId = duplicateItemIdRef.current;
    setDuplicateInfo(null);
    duplicateItemIdRef.current = null;
    if (itemId) {
      fileUpload.updateItem(itemId, { status: 'skipped', progress: 0 });
    }
    void runQueue();
  }, [fileUpload, runQueue]);

  /** Abort the in-flight upload (if any), stop the queue and close. */
  const handleCancel = useCallback(() => {
    stopRef.current = true;
    abortRef.current?.abort();
    onClose();
  }, [onClose]);

  return {
    // Mode
    isEditMode,

    // Form state
    formState: documentForm.formState,
    handleFormChange: documentForm.handleFormChange,
    handleSelectChange: documentForm.handleSelectChange,

    // File handling
    selectedFile: fileUpload.selectedFile,
    handleFileSelect: fileUpload.handleFileSelect,
    handleFileBrowse: fileUpload.handleFileBrowse,
    handleFileRemove: fileUpload.handleFileRemove,
    uploadProgress: fileUpload.uploadProgress,
    uploadingFile: fileUpload.uploadingFile,
    fileInputRef: fileUpload.fileInputRef,
    fileError: fileUpload.fileError,
    maxFileSizeMB: fileUpload.maxFileSizeMB,

    // Multi-file queue
    uploadItems: fileUpload.items,
    uploadSummary,
    handleFilesAdded: fileUpload.addFiles,
    handleRemoveUploadItem: fileUpload.removeItem,
    handleUploadItemTitleChange,
    showTitleField,
    fileAction,
    handleCancel,

    // Tab state
    activeTab,
    handleTabChange,

    // Submission
    handleSubmit,
    isSubmitting,
    submitError,

    // Duplicate-upload resolution
    duplicateInfo,
    resolveDuplicate,
    cancelDuplicate,

    // Validation
    isValid,

    // Data loading
    fullDocument,
    isLoadingDocument,

    // Visibility and sharing
    handleVisibilityChange: documentForm.handleVisibilityChange,
    handleSharingChange: documentForm.handleSharingChange
  };
};
